import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material';

import { CalendarSyncMapping } from 'services/selfSchedulingServices';
import { ERROR_TEXT_COLOR, StatusChip, formatDate } from './syncStatusDisplay';

/** MM-1168: week day names by index (0 = Sunday), as user-mgmt stores them. */
const WEEK_DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** MM-1168: columns of the nested profile mapping table. */
const MAPPING_COLUMNS = [
  'Profile',
  'Start',
  'End',
  'Window',
  'Slot length',
  'Week days',
  'Default room',
  'Secondary room',
  'Status',
  'Last completed',
];

/** MM-1168: minutes as shown in the table, or '-' when not set. */
const minutes = (value: number | null) => (value ? `${value} min` : '-');

/** MM-1168: week day indexes as short day names, or '-' when none. */
const dayNames = (days: number[]) => (days.length ? days.map((day) => WEEK_DAYS[day] ?? day).join(', ') : '-');

/**
 * MM-1168: the profile mappings (appointment templates) of one location + provider, each with its
 * template details, its own rebuild status and - on a line under it - its error, so it is clear
 * exactly which profile failed and why.
 * mappings - the pair's mappings
 */
const ProfileMappingsTable = ({ mappings }: { mappings: CalendarSyncMapping[] }) => {
  if (!mappings.length) {
    return (
      <Typography variant='body2' color='text.secondary' sx={{ p: 2 }}>
        No profile mapping details available.
      </Typography>
    );
  }
  return (
    <Box sx={{ m: 1, ml: 4, border: '1px solid #EAECF0', borderRadius: 1, backgroundColor: '#FCFCFD' }}>
      <Table size='small'>
        <TableHead>
          <TableRow>
            {MAPPING_COLUMNS.map((column) => (
              <TableCell key={column} sx={{ fontWeight: 600, whiteSpace: 'nowrap', color: 'text.secondary' }}>
                {column}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {mappings.map((mapping) => {
            // MM-1169: RETRY_LIMIT_REACHED is an error too
            const failed = mapping.rebuildStatus === 'FAILED' || mapping.rebuildStatus === 'RETRY_LIMIT_REACHED';
            const hasError = failed && !!mapping.rebuildMessage;
            const cellSx = hasError ? { borderBottom: 'none' } : undefined;
            return [
              <TableRow key={mapping.mappingId} sx={{ backgroundColor: failed ? '#FEF3F2' : undefined }}>
                <TableCell sx={{ ...cellSx, fontWeight: 500 }}>{mapping.profileName ?? mapping.profileId}</TableCell>
                <TableCell sx={cellSx}>{mapping.startTime ?? '-'}</TableCell>
                <TableCell sx={cellSx}>{mapping.endTime ?? '-'}</TableCell>
                <TableCell sx={cellSx}>{minutes(mapping.windowMinutes)}</TableCell>
                <TableCell sx={cellSx}>{minutes(mapping.slotDurationMinutes)}</TableCell>
                <TableCell sx={cellSx}>{dayNames(mapping.weekDays)}</TableCell>
                <TableCell sx={cellSx}>{mapping.defaultExamRoom ?? '-'}</TableCell>
                <TableCell sx={cellSx}>{mapping.secondaryExamRoom ?? '-'}</TableCell>
                <TableCell sx={cellSx}>
                  <StatusChip status={mapping.rebuildStatus} />
                </TableCell>
                <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap' }}>{formatDate(mapping.lastCompletedDate)}</TableCell>
              </TableRow>,
              hasError ? (
                <TableRow key={`${mapping.mappingId}-error`} sx={{ backgroundColor: '#FEF3F2' }}>
                  <TableCell colSpan={MAPPING_COLUMNS.length} sx={{ pt: 0 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, color: ERROR_TEXT_COLOR }}>
                      <ErrorOutlineIcon fontSize='small' />
                      {/* MM-1169: one line per failed attempt */}
                      <Typography variant='body2' sx={{ whiteSpace: 'pre-line' }}>
                        {mapping.rebuildMessage}
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : null,
            ];
          })}
        </TableBody>
      </Table>
    </Box>
  );
};

export default ProfileMappingsTable;
